import { SCOPES, DISCOVERY_DOCS, CLIENT_ID } from "../constants";
import { DriveAuthSession, DriveFile } from "../types";

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

let tokenClient: any;
const DEV_AUTH_BYPASS_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
const DEV_AUTH_SESSION: DriveAuthSession = {
  accessToken: "dev_access_token",
  idToken: "dev_id_token",
  expiresAt: Date.now() + 24 * 60 * 60 * 1000,
  userSub: "dev_user_1",
  email: "dev_user_1@local.dev",
};

export const isDevAuthBypassEnabled = (): boolean => DEV_AUTH_BYPASS_ENABLED;
export const getDevAuthSession = (): DriveAuthSession => ({ ...DEV_AUTH_SESSION, expiresAt: Date.now() + 24 * 60 * 60 * 1000 });

const setGapiToken = (accessToken: string) => {
  if (!window.gapi?.client) {
    throw new Error("Google API client is not initialized.");
  }
  window.gapi.client.setToken({ access_token: accessToken });
};

export const initGapiClient = async (): Promise<void> => {
  if (DEV_AUTH_BYPASS_ENABLED) {
    return;
  }
  return new Promise((resolve, reject) => {
    if (window.gapi) {
      window.gapi.load("client", async () => {
        try {
          await window.gapi.client.init({ discoveryDocs: DISCOVERY_DOCS });
          resolve();
        } catch (e) {
          reject(e);
        }
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.onload = () => {
      window.gapi.load("client", async () => {
        try {
          await window.gapi.client.init({ discoveryDocs: DISCOVERY_DOCS });
          resolve();
        } catch (e) {
          reject(e);
        }
      });
    };
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });
};

const waitForGoogleAccounts = () =>
  new Promise<void>((resolve, reject) => {
    if (window.google?.accounts) return resolve();

    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.google?.accounts) {
        clearInterval(interval);
        resolve();
      }
      if (attempts > 50) {
        clearInterval(interval);
        reject(new Error("Google Identity Services script failed to load."));
      }
    }, 100);
  });

const parseJwtPayload = (jwt: string): Record<string, any> | null => {
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const fetchUserInfo = async (
  accessToken: string,
  idToken?: string,
): Promise<{ sub: string; email?: string; idToken?: string }> => {
  // If we have an ID token, parse it directly (faster, no API call)
  if (idToken) {
    const payload = parseJwtPayload(idToken);
    if (payload?.sub) {
      return { sub: payload.sub, email: payload.email, idToken };
    }
  }

  // Fallback: fetch from userinfo endpoint
  const response = await fetch(
    "https://www.googleapis.com/oauth2/v3/userinfo",
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch user info from Google (${response.status}): ${errorText || "Check that your OAuth scopes include openid, profile, and email"}`,
    );
  }
  const data = await response.json();
  if (!data?.sub) {
    throw new Error("Missing user ID from Google userinfo endpoint.");
  }
  return { sub: data.sub, email: data.email };
};

export const signInToGoogle = async (): Promise<DriveAuthSession> => {
  if (DEV_AUTH_BYPASS_ENABLED) {
    return getDevAuthSession();
  }

  await waitForGoogleAccounts();

  const { accessToken, idToken } = await new Promise<{
    accessToken: string;
    idToken?: string;
  }>((resolve, reject) => {
    if (!CLIENT_ID) {
      reject(new Error("Client ID is missing in constants.ts"));
      return;
    }

    try {
      tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: (response: any) => {
          if (response.error) {
            reject(response);
            return;
          }
          resolve({
            accessToken: response.access_token,
            idToken: response.id_token,
          });
        },
      });

      tokenClient.requestAccessToken({ prompt: "consent" });
    } catch (e) {
      reject(e);
    }
  });

  const userInfo = await fetchUserInfo(accessToken, idToken);
  const expiresAt = Date.now() + 55 * 60 * 1000;

  return {
    accessToken,
    idToken: userInfo.idToken || idToken,
    expiresAt,
    userSub: userInfo.sub,
    email: userInfo.email,
  };
};

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const shouldRetryDriveError = (error: any): boolean => {
  const code = Number(
    error?.status || error?.result?.error?.code || error?.code,
  );
  return code === 429 || code === 408 || (code >= 500 && code < 600);
};

const withDriveRetry = async <T>(
  operation: () => Promise<T>,
  maxAttempts: number = 5,
): Promise<T> => {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      if (!shouldRetryDriveError(error) || attempt === maxAttempts) {
        throw error;
      }
      // Exponential backoff (250ms, 500ms, 1s, 2s, ...)
      await delay(250 * 2 ** (attempt - 1));
    }
  }
  throw lastError;
};

export interface DriveFolderImportOptions {
  maxItems?: number;
}

export interface DriveFolderImportResult {
  files: DriveFile[];
  skippedDueToPermissions: number;
  stoppedAtLimit: boolean;
}

const resolveFolderTraversalRoot = async (
  rootId: string,
): Promise<{ folderId: string; skipped: number }> => {
  try {
    const response: any = await withDriveRetry(() =>
      window.gapi.client.drive.files.get({
        fileId: rootId,
        fields: "id,mimeType,shortcutDetails(targetId,targetMimeType)",
        supportsAllDrives: true,
      }),
    );
    const file = response?.result || {};
    const mimeType = file?.mimeType || "";
    if (mimeType === "application/vnd.google-apps.folder") {
      return { folderId: file.id || rootId, skipped: 0 };
    }
    if (
      mimeType === "application/vnd.google-apps.shortcut" &&
      file.shortcutDetails?.targetId &&
      file.shortcutDetails?.targetMimeType === "application/vnd.google-apps.folder"
    ) {
      return { folderId: file.shortcutDetails.targetId, skipped: 0 };
    }
    return { folderId: rootId, skipped: 1 };
  } catch (error) {
    if (isPermissionError(error)) {
      return { folderId: rootId, skipped: 1 };
    }
    throw error;
  }
};

const isPermissionError = (error: any): boolean => {
  const code = Number(
    error?.status || error?.result?.error?.code || error?.code,
  );
  return code === 401 || code === 403;
};

const isMediaMimeType = (mimeType?: string): boolean =>
  Boolean(mimeType?.startsWith("image/") || mimeType?.startsWith("video/"));

const resolveShortcutTarget = async (
  shortcutTargetId: string,
): Promise<{
  id: string;
  name: string;
  mimeType: string;
  thumbnailLink?: string;
} | null> => {
  try {
    const response: any = await withDriveRetry(() =>
      window.gapi.client.drive.files.get({
        fileId: shortcutTargetId,
        fields: "id,name,mimeType,thumbnailLink",
        supportsAllDrives: true,
      }),
    );
    return response?.result || null;
  } catch {
    return null;
  }
};

const normalizeImportedDriveFile = (file: any): DriveFile => ({
  id: file.id,
  name: file.name,
  mimeType: file.mimeType,
  thumbnailLink: file.thumbnailLink,
  webContentLink: file.webContentLink,
  webViewLink: file.webViewLink,
});

export const listFolderMediaFilesDetailed = async (
  accessToken: string,
  folderId: string,
  options: DriveFolderImportOptions = {},
): Promise<DriveFolderImportResult> => {
  setGapiToken(accessToken);

  const mediaFileMap = new Map<string, DriveFile>();
  const root = await resolveFolderTraversalRoot(folderId);
  const foldersToScan = [root.folderId];
  const visitedFolders = new Set<string>();
  let skippedDueToPermissions = root.skipped;
  let stoppedAtLimit = false;
  const maxItems = Number.isFinite(options.maxItems)
    ? Math.max(0, Number(options.maxItems))
    : Number.POSITIVE_INFINITY;

  while (foldersToScan.length > 0) {
    const currentFolderId = foldersToScan.pop()!;
    if (visitedFolders.has(currentFolderId)) continue;
    visitedFolders.add(currentFolderId);

    let pageToken: string | undefined;
    const query = `'${currentFolderId}' in parents and trashed = false`;

    do {
      let response: any;
      try {
        response = await withDriveRetry(() =>
          window.gapi.client.drive.files.list({
            q: query,
            fields:
              "nextPageToken, files(id, name, mimeType, thumbnailLink, webContentLink, webViewLink, shortcutDetails(targetId,targetMimeType), driveId)",
            pageSize: 1000,
            pageToken,
            supportsAllDrives: true,
            includeItemsFromAllDrives: true,
          }),
        );
      } catch (error) {
        if (isPermissionError(error)) {
          skippedDueToPermissions += 1;
          break;
        }
        throw error;
      }

      const result = response?.result || {};
      for (const file of result.files || []) {
        if (mediaFileMap.size >= maxItems) {
          stoppedAtLimit = true;
          break;
        }

        const mimeType = file.mimeType || "";
        const shortcutTargetId = file.shortcutDetails?.targetId as
          | string
          | undefined;
        let shortcutTargetMimeType = file.shortcutDetails?.targetMimeType as
          | string
          | undefined;

        if (mimeType === "application/vnd.google-apps.folder") {
          foldersToScan.push(file.id);
          continue;
        }

        if (
          mimeType === "application/vnd.google-apps.shortcut" &&
          shortcutTargetId
        ) {
          if (!shortcutTargetMimeType) {
            const target = await resolveShortcutTarget(shortcutTargetId);
            shortcutTargetMimeType = target?.mimeType;
            if (target && isMediaMimeType(target.mimeType)) {
              mediaFileMap.set(target.id, normalizeImportedDriveFile(target));
            }
          }

          if (shortcutTargetMimeType === "application/vnd.google-apps.folder") {
            foldersToScan.push(shortcutTargetId);
          } else if (isMediaMimeType(shortcutTargetMimeType)) {
            mediaFileMap.set(shortcutTargetId, {
              ...normalizeImportedDriveFile(file),
              id: shortcutTargetId,
              mimeType: shortcutTargetMimeType,
            });
          }
          continue;
        }

        if (isMediaMimeType(mimeType)) {
          mediaFileMap.set(file.id, normalizeImportedDriveFile(file));
        }
      }

      if (mediaFileMap.size >= maxItems) {
        stoppedAtLimit = true;
        break;
      }
      pageToken = result.nextPageToken;
    } while (pageToken);

    if (stoppedAtLimit) break;
  }

  return {
    files: Array.from(mediaFileMap.values()),
    skippedDueToPermissions,
    stoppedAtLimit,
  };
};

export const listFolderMediaFiles = async (
  accessToken: string,
  folderId: string,
): Promise<DriveFile[]> => {
  const result = await listFolderMediaFilesDetailed(accessToken, folderId);
  return result.files;
};

const DRIVE_UPLOADS_FOLDER_NAME = "Refract Uploads";
let cachedUploadsFolderId: string | null = null;

const googleFetch = async (
  accessToken: string,
  input: string,
  init: RequestInit = {},
): Promise<Response> => {
  const headers = new Headers(init.headers || {});
  headers.set("Authorization", `Bearer ${accessToken}`);
  return fetch(input, { ...init, headers });
};

const ensureUploadsFolderId = async (accessToken: string): Promise<string> => {
  if (cachedUploadsFolderId) return cachedUploadsFolderId;

  const query = [
    "mimeType='application/vnd.google-apps.folder'",
    "trashed=false",
    "name='Refract Uploads'",
  ].join(" and ");
  const searchUrl =
    "https://www.googleapis.com/drive/v3/files" +
    `?q=${encodeURIComponent(query)}` +
    "&fields=files(id,name)" +
    "&pageSize=10" +
    "&supportsAllDrives=true" +
    "&includeItemsFromAllDrives=true";
  const existing = await googleFetch(accessToken, searchUrl);
  if (existing.ok) {
    const json = await existing.json();
    const found = json?.files?.[0]?.id;
    if (found) {
      cachedUploadsFolderId = found;
      return found;
    }
  }

  const createRes = await googleFetch(
    accessToken,
    "https://www.googleapis.com/drive/v3/files?supportsAllDrives=true",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: DRIVE_UPLOADS_FOLDER_NAME,
        mimeType: "application/vnd.google-apps.folder",
      }),
    },
  );
  if (!createRes.ok) {
    const detail = await createRes.text().catch(() => "");
    throw new Error(
      `Failed to create Drive uploads folder (${createRes.status}): ${detail}`,
    );
  }
  const created = await createRes.json();
  cachedUploadsFolderId = created.id;
  return created.id;
};

const findExistingUploadedFileByHash = async (
  accessToken: string,
  contentHash: string,
  sizeBytes: number,
): Promise<DriveFile | null> => {
  const escapedHash = contentHash.replace(/'/g, "\\'");
  const query = [
    "trashed=false",
    `appProperties has { key='refractHash' and value='${escapedHash}' }`,
    `appProperties has { key='refractSize' and value='${sizeBytes}' }`,
  ].join(" and ");
  const url =
    "https://www.googleapis.com/drive/v3/files" +
    `?q=${encodeURIComponent(query)}` +
    "&fields=files(id,name,mimeType,thumbnailLink,webContentLink,webViewLink)" +
    "&pageSize=1" +
    "&supportsAllDrives=true" +
    "&includeItemsFromAllDrives=true";
  const response = await googleFetch(accessToken, url);
  if (!response.ok) return null;
  const json = await response.json();
  const first = json?.files?.[0];
  return first ? normalizeImportedDriveFile(first) : null;
};

export const uploadLocalFileToDrive = async (
  accessToken: string,
  file: File,
  options: { contentHash: string; mimeType?: string },
): Promise<DriveFile> => {
  const mimeType = options.mimeType || file.type || "application/octet-stream";
  const existing = await findExistingUploadedFileByHash(
    accessToken,
    options.contentHash,
    file.size,
  );
  if (existing) {
    return existing;
  }

  const folderId = await ensureUploadsFolderId(accessToken);
  const metadata = {
    name: file.name,
    mimeType,
    parents: [folderId],
    appProperties: {
      refractHash: options.contentHash,
      refractSize: String(file.size),
      refractSource: "local-upload",
    },
  };

  const form = new FormData();
  form.append(
    "metadata",
    new Blob([JSON.stringify(metadata)], { type: "application/json" }),
  );
  form.append("file", file, file.name);

  const uploadRes = await googleFetch(
    accessToken,
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true&fields=id,name,mimeType,thumbnailLink,webContentLink,webViewLink",
    {
      method: "POST",
      body: form,
    },
  );
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => "");
    throw new Error(
      `Failed to upload local file to Drive (${uploadRes.status}): ${detail}`,
    );
  }
  const uploaded = await uploadRes.json();
  return normalizeImportedDriveFile(uploaded);
};

export const signOut = async () => {
  if (DEV_AUTH_BYPASS_ENABLED) {
    return;
  }
  try {
    if (!window.gapi?.client) return;

    const token = window.gapi.client.getToken();
    if (token !== null) {
      try {
        window.google.accounts.oauth2.revoke(token.access_token);
      } catch (err) {
        console.warn("Error revoking token:", err);
      }
      window.gapi.client.setToken("");
    }
  } catch (err) {
    console.warn("Error during sign out:", err);
  }
};
